import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Ban, Edit, Eye, Plus, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { Input } from '@/components/ui/input';
import {
    InfiniteScrollListEmptyState,
    InfiniteScrollListErrorState,
    InfiniteScrollListLoadingState,
} from '@/components/ui/infinite-scroll-list-state';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { authApi, permissionsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type { ActiveRoleAssignmentResponse, PermissionsBundlePublic } from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { PolicyPermissionBundleModal } from './PolicyPermissionBundleModal';

export const PolicyPermissionBundlesCardOpaqueCursorSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1);

interface PolicyPermissionBundlesCardProps {
    readonly organizationUuid: string;
    readonly organizationName: string;
    readonly pageTitle?: string;
}

export function PolicyPermissionBundlesCard(
    props: PolicyPermissionBundlesCardProps
): ReactElement {
    const navigate = useNavigate();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyBundlesVersion: number = useDataInvalidationStore(
        (state) => state.policyBundlesVersion
    );

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            staleTime: 30000,
        });

    const canCreateBundles: boolean = hasInternalAdminPrivileges(activeRoleAssignment ?? null);
    const [selectedBundle, setSelectedBundle] = useState<PermissionsBundlePublic | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
    const [pendingDeleteBundle, setPendingDeleteBundle] = useState<PermissionsBundlePublic | null>(
        null
    );

    const deleteMutation = useMutation({
        mutationFn: async (bundleUuid: string) => {
            await permissionsApi.deletePolicyPermissionBundle(bundleUuid);
        },
        onSuccess: () => {
            useDataInvalidationStore.getState().bumpPolicyBundlesVersion();
            setPendingDeleteBundle(null);
        },
    });
    const setActiveMutation = useMutation({
        mutationFn: async (params: {
            permissionsBundleUuid: string;
            isActive: boolean;
        }) => {
            await permissionsApi.updatePolicyPermissionBundle(
                params.permissionsBundleUuid,
                { is_active: params.isActive }
            );
        },
        onSuccess: () => {
            useDataInvalidationStore.getState().bumpPolicyBundlesVersion();
        },
    });

    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'policyBundles',
            `org=${props.organizationUuid}`,
            `role=${activeRoleAssignmentId ?? 'none'}`,
            `v=${policyBundlesVersion}`,
        ],
        [activeRoleAssignmentId, policyBundlesVersion, props.organizationUuid]
    );

    const list = useInfiniteScrollList<PermissionsBundlePublic, string, ReadonlyArray<string>>({
        queryKeyPrefix,
        pageSize: 50,
        fetchPage: async (params) => {
            const response = await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                props.organizationUuid,
                {
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                }
            );
            return { items: response.bundles, pagination: response.pagination };
        },
        parseCursorParam: (pageParam: unknown) => {
            if (pageParam === null || pageParam === undefined) {
                return undefined;
            }
            const parseResult = PolicyPermissionBundlesCardOpaqueCursorSchema.safeParse(pageParam);
            if (!parseResult.success) {
                throw new Error('Expected opaque cursor');
            }
            return parseResult.data;
        },
        enabled: !isActiveRoleAssignmentLoading,
    });

    return (
        <div>
            {props.pageTitle !== undefined ? (
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="text-3xl font-bold">{props.pageTitle}</h1>
                </div>
            ) : null}

            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Permissions Policies</CardTitle>
                    {canCreateBundles ? (
                        <Button
                            onClick={() => {
                                setSelectedBundle(null);
                                setIsCreateModalOpen(true);
                            }}
                            size="sm"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Policy Bundle
                        </Button>
                    ) : null}
                </CardHeader>
                <CardContent>
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <Input
                            placeholder="Search..."
                            value={list.queryText}
                            onChange={(event) => {
                                list.setQueryText(event.target.value);
                            }}
                            className="w-[320px]"
                        />
                    </div>

                    {deleteMutation.error ? (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {deleteMutation.error instanceof Error
                                    ? deleteMutation.error.message
                                    : 'Failed to delete policy bundle'}
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    {setActiveMutation.error ? (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {setActiveMutation.error instanceof Error
                                    ? setActiveMutation.error.message
                                    : 'Failed to update policy bundle'}
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {list.error !== null ? (
                        <InfiniteScrollListErrorState
                            error={list.error}
                            genericErrorLabel="Failed to load policy bundles."
                        />
                    ) : list.isLoading && list.items.length === 0 ? (
                        <InfiniteScrollListLoadingState label="Loading policy bundles..." />
                    ) : list.items.length === 0 ? (
                        <InfiniteScrollListEmptyState label="No policy bundles found." />
                    ) : (
                        <>
                            <div className="space-y-3">
                                {list.items.map((bundle: PermissionsBundlePublic) => (
                                    <div
                                        key={bundle.permissions_bundle_uuid}
                                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex justify-between items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <h4 className="font-medium">{bundle.name}</h4>
                                                    {!bundle.is_active ? (
                                                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                                                            Disabled
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {bundle.description !== null &&
                                                bundle.description.length > 0 ? (
                                                    <div className="text-sm text-muted-foreground space-y-1">
                                                        <div>{bundle.description}</div>
                                                        <div>
                                                            {bundle.permissions.length} permissions
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-muted-foreground space-y-1">
                                                        <div>
                                                            {bundle.permissions.length} permissions
                                                        </div>
                                                    </div>
                                                )}
                                                {bundle.permissions_deserialization_error !==
                                                undefined ? (
                                                    <p className="text-sm text-destructive mt-2">
                                                        Invalid persisted permissions payload. Open
                                                        to review and repair.
                                                    </p>
                                                ) : null}
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    title="View policy bundle"
                                                    onClick={() => {
                                                        setSelectedBundle(bundle);
                                                        setIsViewModalOpen(true);
                                                    }}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    title="Edit policy bundle"
                                                    onClick={() => {
                                                        setSelectedBundle(bundle);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    title={
                                                        bundle.is_active
                                                            ? 'Disable policy bundle'
                                                            : 'Enable policy bundle'
                                                    }
                                                    onClick={() => {
                                                        setActiveMutation.mutate({
                                                            permissionsBundleUuid:
                                                                bundle.permissions_bundle_uuid,
                                                            isActive: !bundle.is_active,
                                                        });
                                                    }}
                                                    disabled={setActiveMutation.isPending}
                                                >
                                                    <Ban className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    title="Delete policy bundle"
                                                    className="text-red-600 hover:text-red-700"
                                                    onClick={() => {
                                                        setPendingDeleteBundle(bundle);
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    title="Manage policy users"
                                                    onClick={() => {
                                                        runInBackground(
                                                            navigate(
                                                                `/organizations/${props.organizationUuid}` +
                                                                    `/permissions-policies/` +
                                                                    `${bundle.permissions_bundle_uuid}/users`
                                                            ),
                                                            () => {}
                                                        );
                                                    }}
                                                >
                                                    <User className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div ref={list.loadMoreSentinelRef} className="h-8" />
                            {list.isFetching ? (
                                <InfiniteScrollListLoadingState label="Loading policy bundles..." />
                            ) : null}
                        </>
                    )}
                </CardContent>
            </Card>

            <PolicyPermissionBundleModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                mode="create"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={null}
            />

            <PolicyPermissionBundleModal
                open={isViewModalOpen}
                onOpenChange={(open) => {
                    setIsViewModalOpen(open);
                    if (!open) {
                        setSelectedBundle(null);
                    }
                }}
                mode="view"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={selectedBundle}
            />

            <PolicyPermissionBundleModal
                open={isEditModalOpen}
                onOpenChange={(open) => {
                    setIsEditModalOpen(open);
                    if (!open) {
                        setSelectedBundle(null);
                    }
                }}
                mode="edit"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={selectedBundle}
            />

            <ConfirmationModal
                open={pendingDeleteBundle !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingDeleteBundle(null);
                    }
                }}
                message="Delete this policy bundle?"
                confirmLabel="Delete"
                onConfirm={async () => {
                    const bundleToDelete = pendingDeleteBundle;
                    if (bundleToDelete === null) {
                        return;
                    }
                    await deleteMutation.mutateAsync(bundleToDelete.permissions_bundle_uuid);
                }}
                onCancel={() => {
                    setPendingDeleteBundle(null);
                }}
                isLoading={deleteMutation.isPending}
            />
        </div>
    );
}
